import { InfrastructureArt } from './InfrastructureArt';
import { dialogTabStops } from './dialog-tab-stops';
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * The cluster screen. One page, and everything on it is a thing or a number.
 *
 * The screen this replaces was prose. It explained roles, it explained pairing,
 * it explained what a helper was — and an operator still could not find the
 * control that switched it on, or tell at a glance which desk was busy. So the
 * rules here are:
 *
 * - **Every state answers one question.** Not in a cluster: create or join. In
 *   one: who is here and what are they doing.
 * - **Capacity is drawn, not described.** A machine that will take four pages at
 *   once and is doing two is four pips with two lit. Nobody reads that; they see
 *   it.
 * - **Nothing about how the product is built.** No model, no runtime, no
 *   library. Where two machines disagree the screen says they disagree and that
 *   matching versions is the fix, which is the only part anybody can act on.
 * - **The code is read, never typed.** It is shown large on the machine asking
 *   and again in the admit prompt, and a person confirms they match.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  CircleSlash,
  Copy,
  Cpu,
  Hourglass,
  LoaderCircle,
  LogOut,
  Monitor,
  Network,
  Pause,
  Play,
  Plus,
  ShieldAlert,
  Users,
  X,
  Zap,
} from 'lucide-react';

import type {
  ClusterJoinRequest,
  ClusterMember,
  ClusterOutcome,
  ClusterView,
  ClusterWorkKind,
  NearbyCluster,
} from '../shared/desktop-api';
import './cluster-workspace.css';

/**
 * The bridge, or a refusal.
 *
 * Every call on this screen changes membership or reads it, and there is no
 * sensible half-answer to "who is in this group" from a renderer that has no
 * main process behind it. So the absence is an error once, here, rather than an
 * optional chain at every call site that would quietly do nothing.
 */
const api = () => {
  const bridge = window.capturePointDesktop;
  if (!bridge) throw new Error('This screen is not connected to the rest of the application.');
  return bridge;
};

/** What each kind of work is called to an operator. Never the wire name. */
const workLabel: Record<ClusterWorkKind, string> = {
  ocr: 'Reading pages',
  boundary: 'Finding document starts',
  extract: 'Pulling out values',
};

/** The short form, for a card that has three of them side by side. */
const workShort: Record<ClusterWorkKind, string> = {
  ocr: 'Read pages',
  boundary: 'Find document starts',
  extract: 'Read field values',
};

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** "Aug 3" — the day counting started, beside the numbers counted since it. */
const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });

/**
 * An identity code, in the size it has to be read at.
 *
 * Split on its own hyphens so somebody can say it down a corridor without losing
 * their place, and selectable, because the other way this gets across a room is
 * a photograph of the screen.
 */
const IdentityCode = ({ code, size = 'normal' }: { code: string; size?: 'normal' | 'large' }) => (
  <span
    className={`cluster-code ${size === 'large' ? 'cluster-code-large' : ''}`}
    aria-label={code}
  >
    {code.split('-').map((group, index) => (
      <span key={index} className="cluster-code-group">
        {group}
      </span>
    ))}
  </span>
);

/**
 * How much of one kind of work a machine is doing, as slots.
 *
 * The whole reason this screen exists in the shape it does. `2 of 4` is a
 * sentence an operator has to read and compare; four pips with two lit is a
 * thing they see. A machine that cannot do this kind at all draws no pips and
 * says why on hover, because zero pips and four unlit pips mean different things
 * and drawing them the same was the old screen's mistake.
 */
const Slots = ({
  inProgress,
  atOnce,
  refusal,
  recentlyFinished,
}: {
  inProgress: number;
  atOnce: number;
  refusal: string | null;
  recentlyFinished: boolean;
}) => {
  if (atOnce === 0)
    return (
      <span className="cluster-slots cluster-slots-none" title={refusal ?? 'It cannot do this.'}>
        <CircleSlash size={14} aria-hidden="true" />
        <span>Unavailable</span>
      </span>
    );
  // The numbers are polled, and a unit can start and finish wholly between two
  // polls. The warm pip is what stops a machine that is genuinely earning its
  // keep from drawing as an idle one whenever the sample lands in a gap.
  const warm = inProgress === 0 && recentlyFinished;
  const title = warm ? 'Between units right now - it finished work in the last minute' : undefined;
  const pip = (index: number, lit: boolean) => (
    <span
      key={index}
      className={`cluster-pip ${lit ? 'lit' : index === 0 && warm ? 'recent' : ''}`}
    />
  );
  // Bounded so an unusually large machine cannot draw a row that wraps the
  // card. Above twelve the same geometry becomes a fill bar — the lit share of
  // twelve — with the true count written beside it, so a nineteen-lane card
  // reads the same way at a glance as a four-lane one and nobody is left
  // comparing a row of pips on one card against bare text on another.
  if (atOnce > 12) {
    const filled = Math.min(
      12,
      Math.max(inProgress > 0 ? 1 : 0, Math.round((inProgress / atOnce) * 12)),
    );
    return (
      <span
        className="cluster-slots"
        aria-label={`${inProgress} of ${atOnce} running`}
        title={title}
      >
        {Array.from({ length: 12 }, (_unused, index) => pip(index, index < filled))}
        <span className="cluster-slots-many">
          {inProgress} / {atOnce}
        </span>
      </span>
    );
  }
  return (
    <span className="cluster-slots" aria-label={`${inProgress} of ${atOnce} running`} title={title}>
      {Array.from({ length: atOnce }, (_unused, index) => pip(index, index < inProgress))}
    </span>
  );
};

/** One computer. This machine's own card is marked and cannot be removed. */
const MemberCard = ({
  member,
  thisComputerName,
  locked,
  onRemove,
}: {
  member: ClusterMember;
  thisComputerName: string;
  locked: boolean;
  onRemove: () => void;
}) => {
  const [confirming, setConfirming] = useState(false);
  const removeTrigger = useRef<HTMLButtonElement | null>(null);
  const keepButton = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!confirming) return;
    const frame = window.requestAnimationFrame(() => keepButton.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [confirming]);
  const keep = () => {
    setConfirming(false);
    window.requestAnimationFrame(() => removeTrigger.current?.focus());
  };
  const done = member.contribution;
  const totals = done
    ? [
        { label: 'pages', value: done.pagesRead },
        { label: 'document starts', value: done.documentStartsFound },
        { label: 'documents', value: done.documentsRead },
      ].filter((entry) => entry.value > 0)
    : [];

  const rateFor = (
    kind: ClusterWorkKind,
  ): { value: number; label: string; title: string } | null => {
    if (kind === 'ocr' && member.pagesPerMinute !== null)
      return {
        value: member.pagesPerMinute,
        label: 'pages/min',
        title: 'Pages this computer finished over the last minute',
      };
    if (kind === 'extract' && member.documentsPerMinute !== null)
      return {
        value: member.documentsPerMinute,
        label: 'documents/min',
        title: 'Documents this computer finished over the last minute',
      };
    return null;
  };

  return (
    <article
      data-member-id={member.id}
      className={`cluster-member cluster-member-${member.state} ${member.isThisComputer ? 'cluster-member-self' : ''}`}
    >
      <header className="cluster-member-top">
        <span className="cluster-computer-mark" aria-hidden="true">
          <Monitor size={21} />
          <span className={`cluster-dot cluster-dot-${member.state}`} />
        </span>
        <div className="cluster-member-name">
          <h3>{member.name}</h3>
          {member.isThisComputer && <span className="cluster-tag">This computer</span>}
        </div>
        {member.fasterHardware && (
          <span
            className="cluster-tag cluster-tag-fast"
            title="Speed boost available on this computer"
            aria-label="Speed boost available"
          >
            <Zap size={16} aria-hidden="true" />
          </span>
        )}
      </header>

      <p className="cluster-member-summary">{member.summary}</p>

      <dl className="cluster-work">
        {member.activity.map((activity) => {
          const rate = rateFor(activity.kind);
          return (
            <div
              key={activity.kind}
              className="cluster-work-row"
              title={activity.refusal ?? workLabel[activity.kind]}
            >
              <dt>
                {workShort[activity.kind]}
                {activity.refusal && (
                  <details className="cluster-work-refusal">
                    <summary>Why unavailable</summary>
                    <p>{activity.refusal}</p>
                  </details>
                )}
              </dt>
              <dd>
                <Slots
                  inProgress={activity.inProgress}
                  atOnce={activity.atOnce}
                  refusal={activity.refusal}
                  recentlyFinished={activity.recentlyFinished}
                />
                {rate && (
                  <span className="cluster-rate" title={rate.title}>
                    <strong>{rate.value.toLocaleString()}</strong> {rate.label}
                  </span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>

      <footer className="cluster-member-foot" aria-label="Work completed for other computers">
        {/*
          Done nothing and never asked look identical in every other field, and
          telling them apart is the whole reason an operator opens this screen
          after buying a second machine.
        */}
        {/*
          Four different things used to end up as "—": never asked, asked and
          finished nothing, keeps no count, and not reachable yet. The first two
          are opposites — one means the cluster is not using this desk, the other
          means everything it was given came back a failure — and an operator
          deciding whether a second machine is earning its keep was shown the
          same dash for both.
        */}
        {!done ? (
          <span className="cluster-done cluster-done-never">No count from it yet</span>
        ) : !done.everAsked ? (
          <span className="cluster-done cluster-done-never">Not asked for anything yet</span>
        ) : (
          <span className="cluster-done">
            {totals.map((entry) => (
              <span key={entry.label}>
                <strong>{entry.value.toLocaleString()}</strong> {entry.label}
              </span>
            ))}
            {/* The frame the numbers were missing: counting starts over
                    when the machine restarts or is admitted again, so a small
                    number is a short ledger, not a lazy machine. */}
            {totals.length > 0 && done.since && (
              <span
                className="cluster-done-since"
                title="Counting started then. It starts over when this computer restarts or is admitted again."
              >
                since {shortDate(done.since)}
              </span>
            )}
            {totals.length === 0 && done.unitsFailed === 0 && (
              <span className="cluster-done-never">Nothing finished for the others yet</span>
            )}
            {/* One phrasing for a failed unit, wherever it is read from,
                    with the machines named. The amber stays: the work was not
                    lost, it was redone on the computer that sent it. */}
            {done.unitsFailed > 0 && (
              <span className="cluster-done-failed">
                couldn&apos;t finish <strong>{done.unitsFailed.toLocaleString()}</strong> -{' '}
                {member.isThisComputer ? 'redone by the others' : `redone on ${thisComputerName}`}
              </span>
            )}
          </span>
        )}
      </footer>
      <details
        className="cluster-member-details"
        onKeyDown={(event) => {
          if (confirming && event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            keep();
          }
        }}
        onToggle={(event) => {
          if (!event.currentTarget.open) setConfirming(false);
        }}
      >
        <summary>
          Computer details <ChevronDown size={14} aria-hidden="true" />
        </summary>
        <div className="cluster-member-details-body">
          <span>
            Identity code <IdentityCode code={member.identityCode} />
          </span>
          <span>Paige {member.appVersion}</span>
          {!member.isThisComputer &&
            (confirming ? (
              <span className="cluster-confirm">
                <button
                  type="button"
                  className="cluster-danger"
                  disabled={locked}
                  onClick={() => {
                    setConfirming(false);
                    onRemove();
                  }}
                >
                  Remove
                </button>
                <button ref={keepButton} type="button" className="cluster-quiet" onClick={keep}>
                  Keep
                </button>
              </span>
            ) : (
              <button
                ref={removeTrigger}
                type="button"
                className="cluster-quiet"
                disabled={locked}
                onClick={() => setConfirming(true)}
                title="Remove this computer from the group"
                aria-label={`Remove ${member.name} from the group`}
              >
                <X size={14} aria-hidden="true" />
                Remove computer
              </button>
            ))}
        </div>
        {confirming && (
          <p className="cluster-warn">
            It stays removed. To bring it back, somebody has to admit it again.
          </p>
        )}
      </details>
    </article>
  );
};

/**
 * A computer asking to join.
 *
 * The code is large and the consequence is stated. Admitting is transitive —
 * every member inherits every other member's judgement — and that is intended
 * behaviour rather than a footnote, so it is said here rather than discovered.
 */
const JoinRequestCard = ({
  request,
  busy,
  locked,
  onAdmit,
  onIgnore,
}: {
  request: ClusterJoinRequest;
  busy: boolean;
  locked: boolean;
  onAdmit: () => void;
  onIgnore: () => void;
}) => (
  <article data-request-id={request.id} className="cluster-request">
    <div className="cluster-request-who">
      <Users size={18} aria-hidden="true" />
      <div>
        <h3>{request.name}</h3>
        <p>wants to join</p>
      </div>
    </div>
    <div className="cluster-request-code">
      <span className="cluster-request-code-label">Match the code on its screen</span>
      <IdentityCode code={request.identityCode} size="large" />
    </div>
    <div className="cluster-request-act">
      {request.refusal ? (
        <p className="cluster-request-refusal">{request.refusal}</p>
      ) : (
        <p>
          Admitting lets it read pages from <strong>every computer here</strong>.
        </p>
      )}
      {/*
        There has to be a way to say no. Without it a request the operator did
        not recognise — or one that cannot be admitted at all, which had no
        control whatever — sat on every member's screen until that machine gave
        up, and the only way to clear it was to admit it.

        Nothing is sent. The asking machine is not told, because answering would
        confirm to an unadmitted computer that this group is here.
      */}
      {/*
        No confirmation, and that is the right way round rather than an
        oversight. Ignoring sends nothing and changes nothing about the asking
        machine — it stops a row occupying this screen, and the machine can ask
        again. Removing a member takes its access away and leaves a tombstone a
        human has to undo, so that is the one that asks twice.
      */}
      <button
        type="button"
        className="cluster-quiet"
        onClick={onIgnore}
        disabled={locked}
        title="Stop showing this request. Nothing is sent, and it can ask again."
      >
        Not now
      </button>
      {!request.refusal && (
        <button type="button" className="cluster-primary" onClick={onAdmit} disabled={locked}>
          {busy ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />}
          Admit
        </button>
      )}
    </div>
  </article>
);

/**
 * The firewall command with its Copy button, wherever a firewall is the story.
 *
 * One component rather than three copies because the three banners that need it
 * — alone and confirmed blocked, asking with nothing arriving, and a member
 * whose broadcasts never land — are the same fact at three moments, and the fix
 * is identical in all of them. Naming the problem without handing over the
 * command leaves an operator to go and look up two port numbers and their own
 * install path; every part of it is a fact this process already holds.
 *
 * Says "Copied" for two seconds. A command an operator is about to carry to
 * somebody else is exactly the thing they need to be sure actually copied.
 */
const FirewallFix = ({ command }: { command: string | null }) => {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  if (!command) return null;
  return (
    <span className="firewall-fix">
      <em>Run this as an administrator, once, on this computer:</em>
      <code>{command}</code>
      <button
        type="button"
        onClick={async () => {
          setCopyState('idle');
          try {
            await navigator.clipboard.writeText(command);
            setCopyState('copied');
            window.setTimeout(() => setCopyState('idle'), 2000);
          } catch {
            setCopyState('error');
          }
        }}
        aria-live="polite"
      >
        <Copy size={12} aria-hidden="true" />
        {copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy'}
      </button>
      {copyState === 'error' && (
        <span
          className="firewall-copy-error"
          role="alert"
          data-telemetry="error:cluster-copy-failed"
        >
          Select the command and copy it manually.
        </span>
      )}
    </span>
  );
};

const NearbyCard = ({
  cluster,
  busy,
  onJoin,
}: {
  cluster: NearbyCluster;
  busy: boolean;
  onJoin: () => void;
}) => (
  <button
    type="button"
    className="cluster-nearby"
    onClick={onJoin}
    disabled={busy || cluster.refusal !== null}
    title={cluster.refusal ?? `Ask to join ${cluster.name}`}
  >
    <span className="cluster-nearby-name">{cluster.name}</span>
    <span className="cluster-nearby-size">{plural(cluster.size, 'computer', 'computers')}</span>
    {cluster.refusal ? (
      <span className="cluster-nearby-refusal">{cluster.refusal}</span>
    ) : (
      <span className="cluster-nearby-go">Join</span>
    )}
  </button>
);

const LeaveClusterDialog = ({
  clusterName,
  memberCount,
  onClose,
  onLeave,
}: {
  clusterName: string;
  memberCount: number;
  onClose: () => void;
  onLeave: () => void;
}) => {
  const dialogRef = useRef<HTMLElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    previousFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() =>
      dialogRef.current?.querySelector<HTMLButtonElement>('.cluster-quiet')?.focus(),
    );
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogTabStops(dialogRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKeyDown);
      if (previousFocus.current?.isConnected) previousFocus.current.focus();
    };
    // Initial focus belongs to this dialog opening, not each live cluster update.
  }, []);
  const lastMember = memberCount === 1;
  return (
    <div
      className="cluster-dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        ref={dialogRef}
        className="cluster-leave-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cluster-leave-title"
        tabIndex={-1}
      >
        <header>
          <span>
            <LogOut size={19} />
          </span>
          <div>
            <small>Leave group</small>
            <h2 id="cluster-leave-title">Leave “{clusterName}”?</h2>
          </div>
        </header>
        <p>
          {lastMember
            ? 'This is the last computer. Leaving removes the group completely; creating it again later makes a new group.'
            : 'This computer will forget the group and stop sharing work. To return, another member must admit it again.'}
        </p>
        <footer>
          <button type="button" className="cluster-quiet" onClick={onClose}>
            Stay in group
          </button>
          <button type="button" className="cluster-danger" onClick={onLeave}>
            <LogOut size={14} /> Leave group
          </button>
        </footer>
      </section>
    </div>
  );
};

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export const CortexaClusterPage = () => {
  const [view, setView] = useState<ClusterView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [loadIssue, setLoadIssue] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [confirmingLeave, setConfirmingLeave] = useState<string | null>(null);
  const nameField = useRef<HTMLInputElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const pageLive = useRef(false);
  const viewRevision = useRef(0);
  const loadInFlight = useRef<number | null>(null);
  const loadAttempt = useRef(0);
  const mutationLatch = useRef(false);
  const mutationAttempt = useRef(0);
  const focusAfterMutation = useRef(false);
  const failureMessage = (reason: unknown, fallback: string) =>
    (reason instanceof Error
      ? reason.message.trim()
      : typeof reason === 'string'
        ? reason.trim()
        : '') || fallback;

  /**
   * A requested snapshot is allowed to fill a gap, but never to move the screen
   * backwards over a newer subscription event. The revision belongs to what the
   * renderer has actually observed, rather than to wall-clock timing across IPC.
   */
  const acceptView = useCallback((next: ClusterView, expectedRevision?: number) => {
    if (!pageLive.current) return false;
    if (expectedRevision !== undefined && viewRevision.current !== expectedRevision) return false;
    viewRevision.current += 1;
    setView(next);
    setLoadIssue(null);
    if (focusAfterMutation.current) {
      focusAfterMutation.current = false;
      window.requestAnimationFrame(() =>
        pageRef.current?.querySelector<HTMLElement>('h1')?.focus(),
      );
    }
    return true;
  }, []);

  const reload = useCallback(async () => {
    // React state does not change until the next render. The ref closes the
    // same-turn double-click window on Try again just as the mutation latch
    // below closes it on membership actions.
    if (loadInFlight.current !== null) return;
    const attempt = ++loadAttempt.current;
    loadInFlight.current = attempt;
    if (pageLive.current) {
      setLoading(true);
      setLoadIssue(null);
    }
    let failedReadRevision = viewRevision.current;
    try {
      const bridge = api();

      // Enabling watching also returns a current view. It may race a pushed
      // change, so it is a revision-gated snapshot rather than an unconditional
      // assignment.
      const watchingRevision = viewRevision.current;
      const watchingView = await bridge.setClusterWatching(true);
      if (!pageLive.current || loadAttempt.current !== attempt) return;
      acceptView(watchingView, watchingRevision);

      const readRevision = viewRevision.current;
      failedReadRevision = readRevision;
      const current = await bridge.getCluster();
      if (!pageLive.current || loadAttempt.current !== attempt) return;
      acceptView(current, readRevision);
      if (pageLive.current && loadAttempt.current === attempt) setLoadIssue(null);
    } catch (reason) {
      if (
        pageLive.current &&
        loadAttempt.current === attempt &&
        viewRevision.current === failedReadRevision
      )
        setLoadIssue(failureMessage(reason, 'Shared processing could not be loaded.'));
    } finally {
      if (loadInFlight.current === attempt) loadInFlight.current = null;
      if (pageLive.current && loadAttempt.current === attempt) setLoading(false);
    }
  }, [acceptView]);

  useEffect(() => {
    pageLive.current = true;
    let bridge: ReturnType<typeof api> | null = null;
    let stop: () => void = () => undefined;
    try {
      bridge = api();
      // Subscribe before asking for any snapshot. Otherwise a change between
      // getCluster() and onClusterChanged() simply never reaches this screen.
      stop = bridge.onClusterChanged((next) => {
        acceptView(next);
      });
      void reload();
    } catch (reason) {
      setLoading(false);
      setLoadIssue(failureMessage(reason, 'Shared processing could not be loaded.'));
    }
    return () => {
      pageLive.current = false;
      loadAttempt.current += 1;
      loadInFlight.current = null;
      mutationAttempt.current += 1;
      stop();
      // Listening costs a socket, so it runs while somebody is looking and
      // stops when they are not — unless the main process keeps it for active
      // membership or an outstanding request.
      if (bridge) void bridge.setClusterWatching(false).catch(() => undefined);
    };
  }, [acceptView, reload]);

  const act = useCallback(
    async (key: string, run: () => Promise<ClusterOutcome | ClusterView>, focusAfter = false) => {
      // `busy` is presentation state and does not synchronously close a second
      // click in the same JavaScript turn. This is the actual single owner.
      if (mutationLatch.current) return;
      mutationLatch.current = true;
      const attempt = ++mutationAttempt.current;
      const startedAtRevision = viewRevision.current;
      if (focusAfter) focusAfterMutation.current = true;
      setBusy(key);
      setRefusal(null);
      try {
        const answer = await run();
        if (!pageLive.current || mutationAttempt.current !== attempt) return;
        if ('ok' in answer) {
          if (answer.ok) {
            acceptView(answer.view, startedAtRevision);
            if (focusAfter) {
              // A pushed cluster update may have won the revision race before
              // this command response. Focus again after the authoritative
              // response so a replaced heading never leaves focus on <body>.
              focusAfterMutation.current = false;
              window.requestAnimationFrame(() =>
                pageRef.current?.querySelector<HTMLElement>('h1')?.focus(),
              );
            }
          } else {
            focusAfterMutation.current = false;
            setRefusal(
              failureMessage(answer.message, 'That cluster action could not be completed.'),
            );
          }
        } else {
          acceptView(answer, startedAtRevision);
          if (focusAfter) {
            focusAfterMutation.current = false;
            window.requestAnimationFrame(() =>
              pageRef.current?.querySelector<HTMLElement>('h1')?.focus(),
            );
          }
        }
      } catch (reason) {
        focusAfterMutation.current = false;
        if (pageLive.current && mutationAttempt.current === attempt)
          setRefusal(failureMessage(reason, 'That cluster action could not be completed.'));
      } finally {
        mutationLatch.current = false;
        if (pageLive.current && mutationAttempt.current === attempt) setBusy(null);
      }
    },
    [acceptView],
  );

  const members = view?.cluster?.members ?? [];
  const working = useMemo(
    () => members.filter((member) => member.state === 'working').length,
    [members],
  );
  // This public projection key binds UI consent, never native authority. A
  // confirmation opened for a departed cluster must not transfer to a new one.
  const membershipKey = view?.cluster
    ? JSON.stringify([
        view.cluster.name,
        view.cluster.createdAt,
        view.cluster.members.find((member) => member.isThisComputer)?.id ?? null,
      ])
    : null;
  useEffect(() => {
    setConfirmingLeave(null);
  }, [membershipKey]);

  if (!view && loadIssue)
    return (
      <div className="cluster-page cluster-workspace">
        <div
          className="cluster-load-failure"
          role="alert"
          data-telemetry="error:cluster-load-failed"
        >
          <ShieldAlert size={18} aria-hidden="true" />
          <span>
            <strong>Shared processing could not be loaded</strong>
            <small>{loadIssue}</small>
          </span>
          <button
            type="button"
            className="cluster-primary"
            onClick={() => void reload()}
            disabled={loading}
          >
            {loading ? <LoaderCircle className="spin" size={15} /> : null}
            Try again
          </button>
        </div>
      </div>
    );

  if (!view)
    return (
      <div className="cluster-page cluster-workspace">
        <div className="cluster-loading">
          <LoaderCircle className="spin" size={20} /> Looking at the network…
        </div>
      </div>
    );

  return (
    <div ref={pageRef} className="cluster-page cluster-workspace">
      <InfrastructureArt kind="cluster" />
      {view.problem && (
        <p className="cluster-problem" role="alert" data-telemetry="error:cluster-start-failed">
          {view.problem}
        </p>
      )}
      {refusal && (
        <p className="cluster-problem" role="alert" data-telemetry="error:cluster-action-failed">
          {refusal}
        </p>
      )}
      {loadIssue && (
        <div
          className="cluster-load-warning"
          role="alert"
          data-telemetry="error:cluster-load-failed"
        >
          <ShieldAlert size={15} aria-hidden="true" />
          <span>
            <strong>Shared processing could not be refreshed.</strong> {loadIssue}
          </span>
          <button
            type="button"
            className="cluster-quiet"
            onClick={() => void reload()}
            disabled={loading || busy !== null}
          >
            {loading ? <LoaderCircle className="spin" size={14} /> : null}
            Try again
          </button>
        </div>
      )}

      {view.cluster ? (
        <>
          {/*
            The head is the page header, and it only exists when there is a
            cluster to head. It used to be an empty flex row in every other
            state — a dead 14px strip whose one job was pushing the real
            heading down — with the actions in it and the title in a second
            header below, so Pause and Leave floated against nothing.
          */}
          <header className="cluster-head">
            <div className="cluster-head-title">
              <p className="cluster-eyebrow">
                <Network size={16} aria-hidden="true" />
                Shared processing
              </p>
              <h1 tabIndex={-1}>{view.cluster.name}</h1>
              <p className="cluster-head-line">
                {plural(members.length, 'computer', 'computers')}
                {view.cluster.participating ? '' : ' · this computer is paused'}
              </p>
            </div>
            <div className="cluster-head-actions">
              <button
                type="button"
                className={`cluster-quiet ${view.cluster.participating ? '' : 'cluster-paused'}`}
                onClick={() =>
                  void act('pause', () =>
                    api().setClusterParticipating(!view.cluster!.participating),
                  )
                }
                disabled={busy !== null}
                title={
                  view.cluster.participating
                    ? "Stop taking the other computers' work. This computer stays a member and keeps doing its own."
                    : "Start taking the other computers' work again."
                }
              >
                {view.cluster.participating ? <Pause size={14} /> : <Play size={14} />}
                {view.cluster.participating ? 'Pause' : 'Resume'}
              </button>
              <button
                type="button"
                className="cluster-quiet"
                onClick={() => setConfirmingLeave(membershipKey)}
                disabled={busy !== null}
                title="Leave this group"
              >
                <LogOut size={14} />
                Leave
              </button>
            </div>
          </header>

          <div className="cluster-local-identity" aria-label="This computer’s identity">
            <Monitor size={22} aria-hidden="true" />
            <div>
              <strong>{view.thisComputerName}</strong>
              <span>This computer</span>
            </div>
            {view.identityCode && (
              <div className="cluster-local-code">
                <span>Identity code</span>
                <IdentityCode code={view.identityCode} />
              </div>
            )}
            <span className="cluster-local-note">
              {view.cluster.participating
                ? 'Helps when free. Your own scanning comes first.'
                : 'Helping is paused. Your own scanning continues.'}
            </span>
          </div>

          {/*
            The one thing this screen can PROVE about the network, so it is
            stated rather than suggested: this computer is holding a conversation
            with a member right now and has never once heard that member's
            broadcasts. Connections get through, announcements do not. Without
            saying it, the only symptom is that machines take longer than they
            should to notice each other coming and going.
          */}
          {view.networkHint === 'peer-unheard' && (
            <p className="cluster-blocked" role="status">
              <ShieldAlert size={16} aria-hidden="true" />
              <span>
                <strong>This network is blocking how these computers find each other.</strong> They
                are working together anyway - but until Paige is allowed through the firewall, they
                will be slow to notice each other arriving and leaving.
                <FirewallFix command={view.firewallFix} />
              </span>
            </p>
          )}

          {view.requests.length > 0 && (
            <section className="cluster-requests">
              {view.requests.map((request) => (
                <JoinRequestCard
                  key={request.id}
                  request={request}
                  busy={busy === request.id}
                  locked={busy !== null}
                  onAdmit={() =>
                    void act(request.id, () => api().admitIntoCluster(request.id), true)
                  }
                  onIgnore={() =>
                    void act(request.id, () => api().ignoreClusterRequest(request.id), true)
                  }
                />
              ))}
            </section>
          )}

          <div className="cluster-section-title">
            <h2>Computers</h2>
            <span>{working > 0 ? `${working} working now` : 'Current activity'}</span>
          </div>
          <section className="cluster-members" aria-label="Computer activity">
            {members.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                thisComputerName={view.thisComputerName}
                locked={busy !== null}
                onRemove={() => void act(member.id, () => api().removeFromCluster(member.id), true)}
              />
            ))}
          </section>
        </>
      ) : view.asking ? (
        <section className="cluster-asking">
          <span className="cluster-wait-mark">
            <Monitor size={30} aria-hidden="true" />
            <LoaderCircle className="spin" size={18} aria-hidden="true" />
          </span>
          <h1 tabIndex={-1}>Waiting to join {view.asking.clusterName}</h1>
          <p>
            Ask someone in that group to open Shared processing and match this code before admitting{' '}
            <strong>{view.thisComputerName}</strong>.
          </p>
          {view.identityCode && <IdentityCode code={view.identityCode} size="large" />}
          <p className="cluster-asking-note">
            Read it aloud or share a photograph. The request expires after 10 minutes.
          </p>
          {/*
            Otherwise this state is a dead end: an operator watches a spinner
            saying "waiting" while nothing on the network has ever reached this
            process, and there is nothing on the screen to suggest why.
          */}
          {(view.networkHint === 'nothing-heard' || view.networkHint === 'firewall-blocked') && (
            <p className="cluster-blocked" role="status">
              <ShieldAlert size={16} aria-hidden="true" />
              <span>
                <strong>Nothing has reached this computer since it started asking.</strong> That
                usually means the firewall is blocking Paige here, in which case nobody will see
                this request until it is allowed through.
                <FirewallFix command={view.firewallFix} />
              </span>
            </p>
          )}
          <button
            type="button"
            className="cluster-quiet"
            onClick={() => void act('cancel', () => api().cancelClusterJoin(), true)}
            disabled={busy !== null}
          >
            Cancel request
          </button>
        </section>
      ) : (
        <section className="cluster-alone">
          <header className="cluster-title">
            <p className="cluster-eyebrow">
              <Network size={16} aria-hidden="true" />
              Shared processing
            </p>
            <h1 tabIndex={-1}>Share the processing</h1>
            <p>Connect office computers. They help when free; their own scanning comes first.</p>
          </header>
          <div className="cluster-local-identity" aria-label="This computer’s identity">
            <Monitor size={22} aria-hidden="true" />
            <div>
              <strong>{view.thisComputerName}</strong>
              <span>Working on its own</span>
            </div>
            {view.identityCode && (
              <div className="cluster-local-code">
                <span>Identity code</span>
                <IdentityCode code={view.identityCode} />
              </div>
            )}
          </div>

          {/*
            An ask that ran out of time says so, here, where the operator lands
            after it. The ten-minute bound is a security decision — it is the
            window in which an admission is accepted at all — so the answer to
            "nobody got to it in time" is asking again, never waiting longer.
          */}
          {view.askExpired && (
            <p className="cluster-blocked cluster-expired" role="status">
              <Hourglass size={16} aria-hidden="true" />
              <span>
                <strong>
                  Nobody admitted this computer to {view.askExpired.clusterName} within 10 minutes,
                  so it stopped asking.
                </strong>{' '}
                Asking again shows its code on that group's screens for another 10 minutes.
                <span className="cluster-expired-actions">
                  <button
                    type="button"
                    className="cluster-primary"
                    onClick={() =>
                      void act('ask-again', () => api().joinCluster(view.askExpired!.retry), true)
                    }
                    disabled={busy !== null}
                  >
                    {busy === 'ask-again' ? <LoaderCircle className="spin" size={15} /> : null}
                    Ask again
                  </button>
                  <button
                    type="button"
                    className="cluster-quiet"
                    onClick={() =>
                      void act('expired-dismiss', () => api().cancelClusterJoin(), true)
                    }
                    disabled={busy !== null}
                    title="Stop showing this. Nothing is sent."
                  >
                    Not now
                  </button>
                </span>
              </span>
            </p>
          )}

          <div className="cluster-start">
            <form
              className="cluster-create"
              onSubmit={(event) => {
                event.preventDefault();
                if (!name.trim()) {
                  nameField.current?.focus();
                  return;
                }
                void act('create', () => api().createCluster(name.trim()), true);
              }}
            >
              <h2>
                <Plus size={20} aria-hidden="true" />
                Create a group
              </h2>
              {/* The example names the operator's own office, never ours. "Ademero
                  Tampa" read as though they were joining a cluster we run rather
                  than naming one of theirs. */}
              <input
                ref={nameField}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Front Office"
                maxLength={60}
                aria-label="Group name"
              />
              <button
                type="submit"
                className="cluster-primary"
                disabled={busy !== null || !name.trim()}
              >
                {busy === 'create' ? <LoaderCircle className="spin" size={15} /> : null}
                Create
              </button>
            </form>

            <div className="cluster-join">
              <h2>
                <Users size={20} aria-hidden="true" />
                Join a nearby group
              </h2>
              {view.nearby.length === 0 ? (
                view.networkHint === 'looking' ? (
                  // Says what it is doing. A bare spinner over an empty list is
                  // the thing this product does not ship.
                  <p className="cluster-empty">
                    <LoaderCircle className="spin" size={15} aria-hidden="true" />
                    Listening for other computers…
                  </p>
                ) : view.networkHint === 'nothing-heard' ||
                  view.networkHint === 'firewall-blocked' ? (
                  // Nothing at all has reached this process. That is a reason
                  // to SUGGEST the firewall and not to accuse it — an office
                  // with one computer in it looks exactly the same, and this
                  // screen does not know which it is looking at.
                  <p className="cluster-blocked" role="status">
                    <ShieldAlert size={16} aria-hidden="true" />
                    {/*
                          Two sentences for two different amounts of evidence.
                          Silence alone is a SUGGESTION — an office with one
                          computer in it is silent too. Silence plus Windows
                          confirming there is no inbound rule is two facts, and
                          two facts may be stated.
                        */}
                    {view.networkHint === 'firewall-blocked' ? (
                      <span>
                        <strong>Paige is not allowed through this computer's firewall.</strong>{' '}
                        Nothing can reach it until somebody who can change that allows it, for
                        private networks. Nothing else is wrong.
                        {/*
                              The command cannot be run from here — changing the
                              firewall needs elevation and this product installs
                              per user and never elevates — which is exactly why
                              it is worth being able to hand over whole.
                            */}
                        <FirewallFix command={view.firewallFix} />
                      </span>
                    ) : (
                      <span>
                        <strong>Nothing has reached this computer yet.</strong> If there are other
                        computers running Paige here, they are probably being blocked - allow Paige
                        through the firewall on each one, for private networks.
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="cluster-empty">
                    <Cpu size={15} aria-hidden="true" />
                    No groups on this network yet.
                  </p>
                )
              ) : (
                view.nearby.map((nearby) => (
                  <NearbyCard
                    key={nearby.id}
                    cluster={nearby}
                    busy={busy !== null}
                    onJoin={() => void act(nearby.id, () => api().joinCluster(nearby.id), true)}
                  />
                ))
              )}
            </div>
          </div>
        </section>
      )}
      {confirmingLeave !== null && confirmingLeave === membershipKey && view.cluster && (
        <LeaveClusterDialog
          clusterName={view.cluster.name}
          memberCount={view.cluster.members.length}
          onClose={() => setConfirmingLeave(null)}
          onLeave={() => {
            setConfirmingLeave(null);
            void act('leave', () => api().leaveCluster(), true);
          }}
        />
      )}
    </div>
  );
};
